import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Notifications from 'expo-notifications';
import { Platform, Linking } from 'react-native';
import {
  NotificationPreferences,
  DEFAULT_NOTIFICATION_PREFERENCES,
} from '@/types/notifications';
import { ScopedAsyncStorage } from '@/lib/storage';

/**
 * NotificationPreferencesManager
 *
 * Singleton manager for app notification preferences.
 * Handles storage, retrieval, and system permission checks.
 *
 * Storage: AsyncStorage only (device-local)
 * Key: @fimfam/notification-preferences
 */
export class NotificationPreferencesManager {
  private static instance: NotificationPreferencesManager;
  private static readonly STORAGE_KEY = '@fimfam/notification-preferences';

  private preferences: NotificationPreferences = DEFAULT_NOTIFICATION_PREFERENCES;
  private isInitialized = false;
  private storage: ScopedAsyncStorage | null = null;

  private constructor() {}

  /**
   * Get singleton instance
   */
  public static getInstance(): NotificationPreferencesManager {
    if (!NotificationPreferencesManager.instance) {
      NotificationPreferencesManager.instance = new NotificationPreferencesManager();
    }
    return NotificationPreferencesManager.instance;
  }

  /**
   * Set scoped storage and reset initialization
   * Call this when user or device changes
   */
  public setStorage(storage: ScopedAsyncStorage): void {
    this.storage = storage;
    this.isInitialized = false;
    this.preferences = DEFAULT_NOTIFICATION_PREFERENCES;
  }

  /**
   * Initialize manager by loading preferences from storage
   * Should be called early in app lifecycle
   */
  public async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      const stored = this.storage
        ? await this.storage.getItem(NotificationPreferencesManager.STORAGE_KEY)
        : await AsyncStorage.getItem(NotificationPreferencesManager.STORAGE_KEY);

      if (stored) {
        const parsed = JSON.parse(stored) as NotificationPreferences;
        this.preferences = { ...DEFAULT_NOTIFICATION_PREFERENCES, ...parsed };
      } else {
        // First time - save defaults
        await this.savePreferences(DEFAULT_NOTIFICATION_PREFERENCES);
      }

      this.isInitialized = true;
    } catch (error) {
      console.error('[NotificationPreferencesManager] Failed to initialize:', error);
      this.preferences = DEFAULT_NOTIFICATION_PREFERENCES;
      this.isInitialized = true;
    }
  }

  /**
   * Get current notification preferences
   */
  public getPreferences(): NotificationPreferences {
    if (!this.isInitialized) {
      console.warn(
        '[NotificationPreferencesManager] Accessing preferences before initialization'
      );
    }
    return { ...this.preferences };
  }

  /**
   * Update notification preferences
   * @param updates Partial preferences to update
   * @returns Updated preferences
   */
  public async updatePreferences(
    updates: Partial<NotificationPreferences>
  ): Promise<NotificationPreferences> {
    const updated = { ...this.preferences, ...updates };
    await this.savePreferences(updated);
    this.preferences = updated;
    return { ...updated };
  }

  /**
   * Check if notifications are enabled at the system level
   * @returns System notification permission status
   */
  public async checkSystemPermissions(): Promise<{
    granted: boolean;
    canAskAgain: boolean;
  }> {
    try {
      const { status, canAskAgain } = await Notifications.getPermissionsAsync();
      return {
        granted: status === 'granted',
        canAskAgain: canAskAgain ?? true,
      };
    } catch (error) {
      console.error('[NotificationPreferencesManager] Permission check failed:', error);
      return { granted: false, canAskAgain: true };
    }
  }

  /**
   * Request notification permissions from the system
   * @returns Whether permissions were granted
   */
  public async requestPermissions(): Promise<boolean> {
    try {
      const { status } = await Notifications.requestPermissionsAsync();
      return status === 'granted';
    } catch (error) {
      console.error('[NotificationPreferencesManager] Permission request failed:', error);
      return false;
    }
  }

  /**
   * Open system settings for the app
   * Useful when user needs to manually enable notifications
   */
  public async openSystemSettings(): Promise<void> {
    try {
      if (Platform.OS === 'ios') {
        await Linking.openURL('app-settings:');
      } else if (Platform.OS === 'android') {
        await Linking.openSettings();
      }
    } catch (error) {
      console.error('[NotificationPreferencesManager] Failed to open settings:', error);
    }
  }

  /**
   * Check if a specific notification type should be delivered
   * Respects both app-wide toggle and individual toggles
   */
  public shouldDeliverNotification(type: 'morningBrief' | 'eveningRecap'): boolean {
    if (!this.preferences.notificationsEnabled) {
      return false;
    }

    if (type === 'morningBrief') {
      return this.preferences.morningBriefNotificationsEnabled;
    }

    if (type === 'eveningRecap') {
      return this.preferences.eveningRecapNotificationsEnabled;
    }

    return false;
  }

  /**
   * Save preferences to AsyncStorage
   */
  private async savePreferences(prefs: NotificationPreferences): Promise<void> {
    try {
      const data = JSON.stringify(prefs);
      if (this.storage) {
        await this.storage.setItem(NotificationPreferencesManager.STORAGE_KEY, data);
      } else {
        await AsyncStorage.setItem(NotificationPreferencesManager.STORAGE_KEY, data);
      }
    } catch (error) {
      console.error('[NotificationPreferencesManager] Failed to save preferences:', error);
      throw error;
    }
  }

  /**
   * Reset preferences to defaults
   * Useful for testing or user account reset
   */
  public async resetToDefaults(): Promise<void> {
    await this.savePreferences(DEFAULT_NOTIFICATION_PREFERENCES);
    this.preferences = { ...DEFAULT_NOTIFICATION_PREFERENCES };
  }
}
